import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import {
    selectGame,
    selectIsFullscreen,
    selectMobileOrientation,
} from 'src/slices/game.slice';
import { selectIsRemoteTestingEnv, selectTestingMode } from 'src/slices/inspector.slice';
import { selectIsMobileOrTablet, selectScalingTestDevice } from 'src/slices/app.slice';
import { sendEvent } from 'src/utils/tracking';
import { isLoaderExperimentEnabled } from 'src/utils/url';

import SVGIcon from 'src/components/ui/SVGIcon';
import OpenExternalLinkModal from 'src/components/modals/OpenExternalLinkModal';
import MobileGameBar from 'src/components/ui/MobileGameBar';
import ControlBar from 'src/components/ui/ControlBar';
import LoaderProgressOverlay from 'src/components/ui/LoaderProgressOverlay';

import { BOX_SHADOW } from 'src/vars';

const GameEmbedWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 0;
    border-radius: 0;
    background: var(--surface);

    ${({ $isMobileOrTablet }) => $isMobileOrTablet && `
        padding: 0;
        width: 100%;
        height: var(--viewport-height);
        overflow: hidden;

        background-color: var(--surface-muted);
        border-radius: 0;
    `}

    ${({ $isRemoteTesting }) => $isRemoteTesting && `
        width: 100%;
        height: 100%;
        border-radius: 0;
        background: var(--surface);
        transform: scale(1);
        padding: 0;
    `}

    ${({ $isFullscreen }) => $isFullscreen && `
        width: 100%;
        height: 100%;
        border-radius: 0;
        background: var(--surface);

        position: fixed;
        top: 0;
        left: 0;
        padding: 0 0 64px 0; /* control bar height */
        transform: translate(0, 0);
        z-index: 2;
    `}

    &:fullscreen {
        width: 100%;
        height: 100%;
    }
`;

const GameEmbedIFrame = styled.iframe`
    border: none;
    margin: 0;
    padding: 0;
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;

    /* used in fullscreen */
    max-width: var(--max-width, 100%);
    max-height: var(--max-height, 100%);
    transition: max-width .1s linear, max-height .1s linear;

    ${({ $isRemoteTesting, $isMobileOrTablet }) => $isRemoteTesting && `
        border-radius: 0;
        padding-bottom: 0;

        ${$isMobileOrTablet && `
            padding-bottom: 64px;
        `}
    `}

    ${({ $reserveMobileGameBar }) => $reserveMobileGameBar && `
        padding-bottom: 64px;
    `}

    ${({ $isFullscreen }) => $isFullscreen && `
        aspect-ratio: auto;
        border-radius: 0;
        height: 100%;
    `}

    ${({ $isMobileOrTablet, $isVisible }) => $isMobileOrTablet && `
        display: ${$isVisible ? 'block' : 'none'};
        border-radius: 0;

        @media (orientation: landscape) {
            padding-bottom: 0;
        }
    `}
`;

const GameEmbedPlayText = styled.span`
    margin: 16px 0 0;
    font-weight: 700;
    font-size: 20px;
    color: var(--text-primary);
`;

const GameEmbedPlayWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const GameEmbedPlayIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    box-shadow: ${BOX_SHADOW};
    background: var(--surface);
    border-radius: 50%;

    svg {
        width: 20px;
        height: 20px;
        fill: var(--text-disabled);
    }
`;

const StyledSVGIcon = styled(SVGIcon)`
    color: var(--poki-blue);
`;

/* eslint-disable max-len */
// ⚠️ If you change these permissions, make sure to update the ones in the playground/terraform repo as well. Thanks! ⚠️
// This IFRAME_ALLOW is slightly different from the one in playground. It shouldn't contain the fullscreen permission.
const IFRAME_ALLOW = 'autoplay; camera; focus-without-user-activation *; monetization; gamepad; keyboard-map *; xr-spatial-tracking; clipboard-write; web-share; accelerometer; magnetometer; gyroscope; microphone *; screen-wake-lock';
const IFRAME_SANDBOX = 'allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-scripts allow-same-origin allow-downloads';
/* eslint-enable max-len */

export default function GameEmbed() {
    const [iframeSize, setIframeSize] = useState(['100%', '100%']);
    const [hasGameStarted, setHasGameStarted] = useState(false);
    const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);

    const iframeRef = useRef(null);
    const containerRef = useRef(null);

    const game = useSelector(selectGame);
    const isFullscreen = useSelector(selectIsFullscreen);
    const testingMode = useSelector(selectTestingMode);
    const isRemoteTesting = useSelector(selectIsRemoteTestingEnv);
    const isMobileOrTablet = useSelector(selectIsMobileOrTablet);
    const mobileOrientation = useSelector(selectMobileOrientation);
    const scalingTestDevice = useSelector(selectScalingTestDevice);

    const isMobilePlayScreen = !hasGameStarted && isMobileOrTablet;

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsNativeFullscreen(document.fullscreenElement === containerRef.current);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    const handleGameFullScreen = () => {
        setHasGameStarted(true);

        if (containerRef.current) {
            try {
                containerRef.current.requestFullscreen({ navigationUI: 'hide' }).catch(() => { });
            } catch (e) {
                console.error(e);
            }
        }
        sendEvent('remote-session-started');
    };

    const isMobileGamePlaying = hasGameStarted && isMobileOrTablet;
    const showFullscreen = isFullscreen || (isRemoteTesting && isMobileOrTablet) || isMobileGamePlaying;
    const reserveMobileGameBar = isMobileGamePlaying && !isNativeFullscreen;
    const loaderExperimentEnabled = isLoaderExperimentEnabled();

    if (!game.iframeUrl) {
        return null;
    }

    return (
        <GameEmbedWrapper
            ref={containerRef}
            $isMobileOrTablet={isMobileOrTablet}
            $isFullscreen={isFullscreen}
            $isRemoteTesting={isRemoteTesting}
            $testingMode={testingMode}
            $mobileOrientation={mobileOrientation}
            onClick={isMobilePlayScreen ? handleGameFullScreen : undefined}
        >
            {isMobilePlayScreen && (
                <GameEmbedPlayWrapper>
                    <GameEmbedPlayIcon>
                        <StyledSVGIcon icon="play" />
                    </GameEmbedPlayIcon>
                    <GameEmbedPlayText>Play Now!</GameEmbedPlayText>
                </GameEmbedPlayWrapper>
            )}
            {(isMobileOrTablet && hasGameStarted && (
                <>
                    <MobileGameBar mobileOrientation={mobileOrientation} isMobileOrTablet={isMobileOrTablet} />
                </>
            ))}
            {/* Keep it updated with the latest configuration from playground's iframe */}
            <GameEmbedIFrame
                ref={iframeRef}
                $isVisible={hasGameStarted}
                $isMobileOrTablet={isMobileOrTablet}
                $isFullscreen={showFullscreen}
                $reserveMobileGameBar={reserveMobileGameBar}
                $isRemoteTesting={isRemoteTesting}
                $testingMode={testingMode}
                $mobileOrientation={mobileOrientation}
                $scalingTestDevice={scalingTestDevice}
                src={game.iframeUrl}
                allow={IFRAME_ALLOW}
                scrolling="no"
                referrerPolicy="origin"
                sandbox={IFRAME_SANDBOX}
                style={{
                    // we set max-width and max-height only in fullscreen mode
                    '--max-width': iframeSize[0],
                    '--max-height': iframeSize[1],
                }}
            />
            
            {loaderExperimentEnabled && (
                <LoaderProgressOverlay
                    key={game.iframeUrl}
                    hidden={isMobilePlayScreen}
                    iframeRef={iframeRef}
                    iframeUrl={game.iframeUrl}
                    minimumTotalBytes={game.medianDownloadSize}
                />
            )}
            {/* control bar is used only in fullscreen mode */}
            {isFullscreen && (
                <ControlBar game={game} setIframeSize={setIframeSize} />
            )}
            <OpenExternalLinkModal />
        </GameEmbedWrapper>
    );
}

GameEmbed.propTypes = {
    gameModule: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number,
    }).isRequired,
};
